import React, { useEffect, useMemo, useState } from "react";

import AdminWalletFilters from "../../components/admin/wallets/AdminWalletFilters";
import AdminWalletTable from "../../components/admin/wallets/AdminWalletTable";
import AdminWalletDetailsModal from "../../components/admin/wallets/AdminWalletDetailsModal";
import AdminApproveModal from "../../components/admin/wallets/AdminApproveModal";
import AdminRejectModal from "../../components/admin/wallets/AdminRejectModal";

import {
  adminListWalletTransactions,
  adminApproveWalletTransaction,
  adminRejectWalletTransaction,
  adminListWalletTransfers,
  adminApproveWalletTransfer,
  adminRejectWalletTransfer,
} from "../../utils/adminWalletApi";

export default function AdminWalletsPage() {
  // ✅ NEW: which dataset are we moderating?
  const [scope, setScope] = useState("transactions"); // "transactions" | "transfers"

  const [tab, setTab] = useState("pending"); // pending | completed | failed | cancelled
  const [type, setType] = useState(""); // only applies to WalletTransaction
  const [q, setQ] = useState("");

  const [items, setItems] = useState([]);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [detailsTx, setDetailsTx] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);

  const [approveTx, setApproveTx] = useState(null);
  const [openApprove, setOpenApprove] = useState(false);

  const [rejectTx, setRejectTx] = useState(null);
  const [openReject, setOpenReject] = useState(false);

  const status = useMemo(() => tab, [tab]);

  async function load({ reset = false } = {}) {
    const useOffset = reset ? 0 : offset;

    setLoading(true);
    setErr("");

    try {
      let data;

      if (scope === "transactions") {
        data = await adminListWalletTransactions({
          status,
          type,
          q: q?.trim() || "",
          limit,
          offset: useOffset,
        });
      } else {
        // transfers
        data = await adminListWalletTransfers({
          status,
          q: q?.trim() || "",
          limit,
          offset: useOffset,
        });
      }

      const newItems = data.items || [];
      setNextOffset(data.nextOffset ?? null);

      if (reset) {
        setItems(newItems);
        setOffset(0);
      } else {
        setItems((prev) => [...prev, ...newItems]);
      }
    } catch (e) {
      setErr(e.message || "Impossible de charger les opérations wallet.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, status, type]);

  useEffect(() => {
    const id = setTimeout(() => load({ reset: true }), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function refresh() {
    await load({ reset: true });
  }

  async function loadMore() {
    if (nextOffset === null) return;
    setOffset(nextOffset);

    setLoading(true);
    setErr("");

    try {
      let data;

      if (scope === "transactions") {
        data = await adminListWalletTransactions({
          status,
          type,
          q: q?.trim() || "",
          limit,
          offset: nextOffset,
        });
      } else {
        data = await adminListWalletTransfers({
          status,
          q: q?.trim() || "",
          limit,
          offset: nextOffset,
        });
      }

      const newItems = data.items || [];
      setNextOffset(data.nextOffset ?? null);
      setItems((prev) => [...prev, ...newItems]);
    } catch (e) {
      setErr(e.message || "Impossible de charger plus d'éléments.");
    } finally {
      setLoading(false);
    }
  }

  function openTx(tx) {
    setDetailsTx(tx);
    setOpenDetails(true);
  }

  function askApprove(tx) {
    setApproveTx(tx);
    setOpenApprove(true);
  }

  function askReject(tx) {
    setRejectTx(tx);
    setOpenReject(true);
  }

  async function doApprove() {
    if (!approveTx?.reference) return;

    if (scope === "transactions") {
      await adminApproveWalletTransaction(approveTx.reference);
    } else {
      await adminApproveWalletTransfer(approveTx.reference);
    }

    setOpenApprove(false);
    setApproveTx(null);
    await refresh();
  }

  async function doReject(reason) {
    if (!rejectTx?.reference) return;

    if (scope === "transactions") {
      await adminRejectWalletTransaction(rejectTx.reference, reason || "");
    } else {
      await adminRejectWalletTransfer(rejectTx.reference, reason || "");
    }

    setOpenReject(false);
    setRejectTx(null);
    await refresh();
  }

  const subtitle =
    scope === "transactions"
      ? "Approuver / rejeter les opérations wallet (fund, withdraw, loan)."
      : "Approuver / rejeter les transferts wallet-to-wallet (débit/crédit).";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="pt-6 md:ml-64 p-4">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Wallet • Validation Admin
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
            </div>
          </div>

          <AdminWalletFilters
            scope={scope}
            setScope={(s) => {
              setScope(s);
              setOffset(0);
              setItems([]);
              if (s === "transfers") {
                setType("");
                if (tab === "failed") setTab("pending");
              }
            }}
            tab={tab}
            setTab={(t) => {
              setTab(t);
              setOffset(0);
              setItems([]);
            }}
            q={q}
            setQ={setQ}
            type={type}
            setType={setType}
            onRefresh={refresh}
          />
          
          <AdminWalletTable
            scope={scope}
            loading={loading}
            err={err}
            items={items}
            onOpen={openTx}
            onApprove={askApprove}
            onReject={askReject}
            nextOffset={nextOffset}
            onLoadMore={loadMore}
          />
        </div>
      </div>

      <AdminWalletDetailsModal
        open={openDetails}
        tx={detailsTx}
        scope={scope}
        onClose={() => {
          setOpenDetails(false);
          setDetailsTx(null);
        }}
      />

      <AdminApproveModal
        open={openApprove}
        tx={approveTx}
        onClose={() => {
          setOpenApprove(false);
          setApproveTx(null);
        }}
        onConfirm={doApprove}
      />

      <AdminRejectModal
        open={openReject}
        tx={rejectTx}
        onClose={() => {
          setOpenReject(false);
          setRejectTx(null);
        }}
        onConfirm={doReject}
      />
    </div>
  );
}
